/**
 * Support macros in the console: the write client (they ride the Support
 * inbox write gate; the Worker side is `worker/support-macro-writes.ts`), the
 * pure helpers the editor needs (insert a `{{variable}}` at the cursor, list
 * the variables a text uses, group the registry), the labels, and what a
 * refusal tells the person to do next.
 *
 * The actor is never sent: the Worker takes it from the session.
 */
import { postSupportInboxWrite } from "@/lib/support-inbox";
import { inboxWriteFailureMessage } from "@/lib/support-inbox";
import type {
  MacroCategory,
  MacroChannel,
  MacroDetail,
  MacroPreview,
  MacroSendResult,
  MacroStatus,
  MacroVariable,
  MacroVariableGroup,
  SendIdentity,
} from "@/types-support";

const BASE = "/api/admin/support/macros";

// --- pure helpers -------------------------------------------------------------

/**
 * Put `token` where the cursor is, replacing any selected text, and say where
 * the cursor goes: right after the token. Out-of-range or reversed selections
 * are clamped, so a stale cursor can never throw or drop text.
 */
export function insertAtCursor(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  token: string,
): { value: string; cursor: number } {
  const clamp = (position: number) =>
    Math.min(
      Math.max(Number.isFinite(position) ? position : 0, 0),
      value.length,
    );
  const a = clamp(selectionStart);
  const b = clamp(selectionEnd);
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return {
    value: value.slice(0, start) + token + value.slice(end),
    cursor: start + token.length,
  };
}

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;

/** The names a text puts in `{{…}}`, each once, in first-use order. */
export function templateVariables(text: string): string[] {
  const found: string[] = [];
  for (const match of text.matchAll(PLACEHOLDER)) {
    const key = match[1]!;
    if (!found.includes(key)) found.push(key);
  }
  return found;
}

/** The `{{key}}` text a picker inserts. */
export function variableToken(key: string): string {
  return `{{${key}}}`;
}

const GROUP_ORDER: readonly MacroVariableGroup[] = [
  "customer",
  "company",
  "stateFiling",
  "federalTax",
  "links",
  "agent",
];

const GROUP_LABEL: Record<MacroVariableGroup, string> = {
  customer: "Customer",
  company: "Company",
  stateFiling: "State filing",
  federalTax: "Federal tax",
  links: "Links",
  agent: "Agent",
};

/** The registry in the picker's order: known groups first, then anything new. */
export function groupVariables(
  variables: readonly MacroVariable[],
): Array<{ group: MacroVariableGroup; label: string; items: MacroVariable[] }> {
  const groups = new Map<MacroVariableGroup, MacroVariable[]>();
  // An unreadable answer (not a list) groups to nothing instead of throwing.
  for (const variable of Array.isArray(variables) ? variables : []) {
    groups.set(variable.group, [
      ...(groups.get(variable.group) ?? []),
      variable,
    ]);
  }
  const order = [
    ...GROUP_ORDER.filter((group) => groups.has(group)),
    ...[...groups.keys()].filter((group) => !GROUP_ORDER.includes(group)),
  ];
  return order.map((group) => ({
    group,
    label: GROUP_LABEL[group] ?? group,
    items: groups.get(group) ?? [],
  }));
}

// --- labels -------------------------------------------------------------------

export const CATEGORY_LABEL: Record<MacroCategory, string> = {
  onboarding: "Onboarding",
  company_details: "Company details",
  state_filings: "State filings",
  federal_tax: "Federal tax",
  billing: "Billing",
  general: "General",
};
export const CATEGORIES = Object.keys(CATEGORY_LABEL) as MacroCategory[];

export const CHANNEL_LABEL: Record<MacroChannel, string> = {
  reply: "Reply",
  new_email: "New email",
  chat: "Chat",
};
export const CHANNELS = Object.keys(CHANNEL_LABEL) as MacroChannel[];

export const STATUS_LABEL: Record<MacroStatus, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

/** The list's status filter; Active is where it opens. */
export const STATUS_FILTERS = ["active", "draft", "archived", "all"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

// --- writes -------------------------------------------------------------------

export interface MacroFieldsInput {
  name: string;
  category: MacroCategory;
  description: string | null;
  subject: string;
  body: string;
  channels: MacroChannel[];
}

export function createMacro(
  fields: MacroFieldsInput,
  status: "draft" | "active",
): Promise<MacroDetail> {
  return postSupportInboxWrite(BASE, { ...fields, status });
}

export function updateMacro(
  id: string,
  expectedVersion: number,
  changes: Partial<MacroFieldsInput> & {
    status?: "draft" | "active";
    note?: string;
  },
): Promise<MacroDetail> {
  return postSupportInboxWrite(`${BASE}/${encodeURIComponent(id)}/update`, {
    expectedVersion,
    ...changes,
  });
}

export function archiveMacro(
  id: string,
  expectedVersion: number,
): Promise<MacroDetail> {
  return postSupportInboxWrite(`${BASE}/${encodeURIComponent(id)}/archive`, {
    expectedVersion,
  });
}

export function restoreMacro(
  id: string,
  expectedVersion: number,
): Promise<MacroDetail> {
  return postSupportInboxWrite(`${BASE}/${encodeURIComponent(id)}/restore`, {
    expectedVersion,
  });
}

export function duplicateMacro(id: string): Promise<MacroDetail> {
  return postSupportInboxWrite(
    `${BASE}/${encodeURIComponent(id)}/duplicate`,
    {},
  );
}

/** What a preview renders against: a saved macro or the editor's unsaved text. */
export type PreviewSource =
  { macroId: string } | { template: { subject: string; body: string } };

/** No side effects: nothing is sent, logged or saved. */
export function previewMacro(
  source: PreviewSource,
  context: { companyId?: string | null; ticketId?: string | null } = {},
): Promise<MacroPreview> {
  return postSupportInboxWrite(`${BASE}/preview`, {
    ...source,
    ...(context.companyId ? { companyId: context.companyId } : {}),
    ...(context.ticketId ? { ticketId: context.ticketId } : {}),
  });
}

export interface SendMacroDraft {
  macroId: string;
  expectedVersion?: number;
  mode: "reply" | "new_email";
  companyId?: string | null;
  ticketId?: string | null;
  /** One per compose session: the same key twice sends once. */
  idempotencyKey: string;
  sentAs?: SendIdentity;
  setStatus?: "waiting" | "solved" | "open";
  overrides?: { subject?: string; body?: string };
}

export function sendMacro(draft: SendMacroDraft): Promise<MacroSendResult> {
  return postSupportInboxWrite(`${BASE}/send`, {
    macroId: draft.macroId,
    mode: draft.mode,
    idempotencyKey: draft.idempotencyKey,
    ...(draft.expectedVersion !== undefined
      ? { expectedVersion: draft.expectedVersion }
      : {}),
    ...(draft.companyId ? { companyId: draft.companyId } : {}),
    ...(draft.ticketId ? { ticketId: draft.ticketId } : {}),
    ...(draft.sentAs ? { sentAs: draft.sentAs } : {}),
    ...(draft.setStatus ? { setStatus: draft.setStatus } : {}),
    ...(draft.overrides && Object.keys(draft.overrides).length > 0
      ? { overrides: draft.overrides }
      : {}),
  });
}

// --- failures -----------------------------------------------------------------

export const MACRO_CONFLICT_MESSAGE =
  "Someone changed this macro. Reload to see their version.";

/** A save that lost to a newer version (409 from nt-support). */
export function isMacroConflict(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 409;
}

/** What a person reads when a macro write fails: the reason and what to do next. */
export function macroWriteFailureMessage(error: unknown): string {
  if (isMacroConflict(error)) return MACRO_CONFLICT_MESSAGE;
  const failure = error as { status?: number; message?: string } | null;
  if (failure?.status === 404) {
    return `${failure.message ?? "This macro was not found."} Go back to Support → Macros; it may have been removed.`;
  }
  return inboxWriteFailureMessage(error);
}

/** A send's failure: a blocked or opted-out recipient reads differently from a Gmail failure. */
export function macroSendFailureMessage(error: unknown): string {
  const failure = error as {
    status?: number;
    code?: string | null;
    message?: string;
  } | null;
  if (failure?.code === "BLOCKED") {
    return `${failure.message ?? "This email was not sent."} Nothing was sent. Pick another macro or company, or fix the owner's record, then send again.`;
  }
  if (failure?.status === 409) {
    return `${failure.message ?? "The macro changed."} Reload the macro, preview it again and send again.`;
  }
  return inboxWriteFailureMessage(error);
}

// --- preview ------------------------------------------------------------------

/**
 * Whether a send would render exactly this preview. Never true while a
 * variable is missing or unknown or the recipient is refused, whatever the
 * `ready` flag says: the console must not call it ready on a guess.
 */
export function previewReady(preview: MacroPreview): boolean {
  return (
    preview.ready &&
    preview.missing.length === 0 &&
    preview.unknown.length === 0 &&
    preview.refusals.length === 0
  );
}

/** The one-line summary above a preview: ready, or what stands in the way. */
export function previewVerdict(preview: MacroPreview): string {
  if (previewReady(preview)) return "Ready to send.";
  const parts: string[] = [];
  if (preview.missing.length > 0) {
    parts.push(
      `${preview.missing.length} ${preview.missing.length === 1 ? "variable has" : "variables have"} no value`,
    );
  }
  if (preview.unknown.length > 0) {
    parts.push(
      `${preview.unknown.length} unknown ${preview.unknown.length === 1 ? "placeholder" : "placeholders"}`,
    );
  }
  if (preview.refusals.length > 0) parts.push("the recipient is refused");
  return parts.length > 0
    ? `Not ready: ${parts.join(", ")}.`
    : "Not ready to send.";
}

// --- the editor's form ----------------------------------------------------------

export interface MacroForm {
  name: string;
  category: MacroCategory;
  description: string;
  channels: MacroChannel[];
  subject: string;
  body: string;
  /** Archived macros are not edited: restore first. */
  status: "draft" | "active";
}

const NEW_MACRO_FORM: MacroForm = {
  name: "",
  category: "general",
  description: "",
  channels: ["reply"],
  subject: "",
  body: "",
  status: "draft",
};

export function formOfMacro(macro: MacroDetail | null): MacroForm {
  if (macro === null) return { ...NEW_MACRO_FORM, channels: ["reply"] };
  return {
    name: macro.name,
    category: macro.category,
    description: macro.description ?? "",
    channels: CHANNELS.filter((channel) => macro.channels.includes(channel)),
    subject: macro.subject,
    body: macro.body,
    status: macro.status === "active" ? "active" : "draft",
  };
}

/** Why the form cannot be saved yet, in words that say what to change; null when it can. */
export function formProblem(form: MacroForm): string | null {
  if (form.name.trim() === "") return "Give the macro a name.";
  if (form.name.trim().length > 120) {
    return "Shorten the name to 120 characters or fewer.";
  }
  if (form.description.length > 400) {
    return "Shorten the description to 400 characters or fewer.";
  }
  if (form.channels.length === 0) {
    return "Choose at least one channel the macro is used in.";
  }
  if (form.subject.trim() === "") return "Write a subject.";
  if (form.subject.trim().length > 200) {
    return "Shorten the subject to 200 characters or fewer.";
  }
  if (form.body.trim() === "") return "Write the text of the email.";
  if (form.body.length > 20_000) {
    return "Shorten the text to 20,000 characters or fewer.";
  }
  return null;
}

/** The fields that differ from the saved macro; empty when nothing changed. */
export function formChanges(
  macro: MacroDetail,
  form: MacroForm,
): Partial<MacroFieldsInput> & { status?: "draft" | "active" } {
  const saved = formOfMacro(macro);
  const changes: Partial<MacroFieldsInput> & { status?: "draft" | "active" } =
    {};
  if (form.name.trim() !== saved.name) changes.name = form.name.trim();
  if (form.category !== saved.category) changes.category = form.category;
  if (form.description.trim() !== saved.description.trim()) {
    changes.description = form.description.trim() || null;
  }
  if (form.channels.join() !== saved.channels.join()) {
    changes.channels = CHANNELS.filter((c) => form.channels.includes(c));
  }
  if (form.subject.trim() !== saved.subject)
    changes.subject = form.subject.trim();
  if (form.body !== saved.body) changes.body = form.body;
  if (macro.status !== "archived" && form.status !== saved.status) {
    changes.status = form.status;
  }
  return changes;
}

/** The new-macro fields from a form, ready for the create call. */
export function fieldsOfForm(form: MacroForm): MacroFieldsInput {
  return {
    name: form.name.trim(),
    category: form.category,
    description: form.description.trim() || null,
    subject: form.subject.trim(),
    body: form.body,
    channels: CHANNELS.filter((channel) => form.channels.includes(channel)),
  };
}

// --- the ticket's companies ---------------------------------------------------

export interface TicketCompany {
  id: string;
  name: string;
}

/**
 * The companies the requester owns, as the ticket's account context lists
 * them (`requesterContext.companies`), read defensively: a renamed field
 * drops one company, never the composer. A reply may only render against one
 * of these.
 */
export function ticketCompanies(requesterContext: unknown): TicketCompany[] {
  if (
    requesterContext === null ||
    typeof requesterContext !== "object" ||
    !Array.isArray((requesterContext as { companies?: unknown }).companies)
  ) {
    return [];
  }
  const companies: TicketCompany[] = [];
  for (const raw of (requesterContext as { companies: unknown[] }).companies) {
    if (raw === null || typeof raw !== "object") continue;
    const row = raw as { id?: unknown; displayName?: unknown };
    if (typeof row.id !== "string" || row.id.trim() === "") continue;
    if (companies.some((company) => company.id === row.id)) continue;
    companies.push({
      id: row.id,
      name:
        typeof row.displayName === "string" && row.displayName.trim() !== ""
          ? row.displayName
          : row.id,
    });
  }
  return companies;
}

// --- the reply box's macro marker ------------------------------------------------

export interface MacroMarker {
  id: string;
  version: number;
}

/**
 * Which macro a reply started from. Inserting sets it; anything that empties
 * the text (a send, a note, deleting it all) drops it, so a reply typed by
 * hand afterwards never claims a macro.
 */
export function nextMacroMarker(
  current: MacroMarker | null,
  event:
    | { type: "inserted"; marker: MacroMarker }
    | { type: "sent" | "noted" }
    | { type: "edited"; body: string },
): MacroMarker | null {
  switch (event.type) {
    case "inserted":
      return event.marker;
    case "sent":
    case "noted":
      return null;
    case "edited":
      return event.body.trim() === "" ? null : current;
  }
}
